import { useTranslation } from 'react-i18next'
import { mdiArrowRight, mdiCheckboxBlankOutline, mdiFormatText, mdiUndo } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import type { AnnotationTool } from './types'

const PRESET_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6']

interface AnnotationToolbarProps {
  tool: AnnotationTool
  color: string
  onToolChange: (tool: AnnotationTool) => void
  onColorChange: (color: string) => void
  onUndo: () => void
  canUndo: boolean
}

export function AnnotationToolbar({
  tool,
  color,
  onToolChange,
  onColorChange,
  onUndo,
  canUndo,
}: AnnotationToolbarProps) {
  const { t } = useTranslation('feedback')

  const tools: { id: AnnotationTool; icon: React.ReactNode; label: string }[] = [
    { id: 'rect', icon: <AppIcon path={mdiCheckboxBlankOutline} size="sm" />, label: t('tool_rect') },
    { id: 'arrow', icon: <AppIcon path={mdiArrowRight} size="sm" />, label: t('tool_arrow') },
    { id: 'text', icon: <AppIcon path={mdiFormatText} size="sm" />, label: t('tool_text') },
  ]

  return (
    <div className="flex flex-col gap-3 p-3 bg-muted/30 rounded-lg border">
      {/* Tool buttons */}
      <div className="flex flex-col gap-1">
        {tools.map((t_) => (
          <Button
            key={t_.id}
            variant={tool === t_.id ? 'default' : 'ghost'}
            size="sm"
            className="justify-start gap-2"
            onClick={() => onToolChange(t_.id)}
            title={t_.label}
            data-testid={`annotation-tool-${t_.id}`}
          >
            {t_.icon}
            <span className="text-xs">{t_.label}</span>
          </Button>
        ))}
      </div>

      {/* Divider */}
      <div className="border-t" />

      {/* Color picker */}
      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground">Color</p>
        <div className="flex flex-wrap gap-1.5">
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => onColorChange(c)}
              className="w-5 h-5 rounded-full border-2 transition-all hover:scale-110"
              style={{
                backgroundColor: c,
                borderColor: color === c ? 'hsl(var(--foreground))' : 'transparent',
              }}
              title={c}
              data-testid={`color-option-${c.replace('#', '')}`}
            />
          ))}
        </div>
        {/* Custom color input */}
        <input
          type="color"
          value={color}
          onChange={(e) => onColorChange(e.target.value)}
          className="w-full h-6 rounded cursor-pointer border border-border bg-transparent"
          title="Custom color"
          data-testid="annotation-color-custom"
        />
      </div>

      {/* Divider */}
      <div className="border-t" />

      {/* Undo */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onUndo}
        disabled={!canUndo}
        className="justify-start gap-2"
        data-testid="annotation-undo"
      >
        <AppIcon path={mdiUndo} size="sm" />
        <span className="text-xs">{t('undo')}</span>
      </Button>
    </div>
  )
}
